// src/components/expert/VideoListPage.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import VideoPlayer from './VideoPlayer';
import { uploadVideo, deleteVideo } from '../../services/videoStorageService';

function VideoListPage({ currentUser, onBack }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // id de la vidéo à supprimer
  const [deleting, setDeleting] = useState(false);
  const [associateVideo, setAssociateVideo] = useState(null); // vidéo à associer
  const [matches, setMatches] = useState([]);
  const [matchSearch, setMatchSearch] = useState('');
  const [associating, setAssociating] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);

  // ─── Charger les vidéos depuis Supabase ────────────────────────────────────
  useEffect(() => {
    loadVideos();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVideos = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('match_videos')
        .select(`
          *,
          matches (
            id,
            prenomJoueur1, joueur1Nom,
            prenomJoueur2, joueur2Nom,
            dateMatch,
            currentScore
          )
        `)
        .eq('userId', currentUser.id)
        .order('createdAt', { ascending: false });

      if (error) throw error;
      setVideos(data || []);
    } catch (err) {
      console.error('❌ Erreur chargement vidéos:', err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Upload fichier ────────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validation type
    if (!file.name.match(/\.(mp4|mov|avi|mkv|webm)$/i)) {
      setUploadError('Format non supporté. Utilisez MP4, MOV, AVI, MKV ou WebM.');
      return;
    }

    // Validation taille (max 50GB)
    if (file.size > 50 * 1024 * 1024 * 1024) {
      setUploadError('Fichier trop volumineux (max 50GB).');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);
    e.target.value = '';

    try {
      console.log('📤 Début upload:', file.name, (file.size / 1024 / 1024).toFixed(1), 'MB');

      // 1. Créer l'enregistrement en base avec status 'uploading'
      const { data: videoRecord, error: insertError } = await supabase
        .from('match_videos')
        .insert({
          userId:      currentUser.id,
          sourceType:  'upload',
          sourceUrl:   null,
          status:      'uploading',
          fileSizeMb:  parseFloat((file.size / 1024 / 1024).toFixed(1)),
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setUploadProgress(10);

      // 2. Uploader vers R2
      const { storagePath, storageUrl } = await uploadVideo(
        file,
        currentUser.id,
        null,
        (pct) => setUploadProgress(Math.round(10 + pct * 0.85))
      );

      setUploadProgress(95);

      // 3. Mettre à jour l'enregistrement avec le chemin R2
      const { error: updateError } = await supabase
        .from('match_videos')
        .update({
          storagePath,
          storageUrl,
          status: 'pending',
        })
        .eq('id', videoRecord.id);

      if (updateError) throw updateError;

      setUploadProgress(100);
      console.log('✅ Vidéo uploadée et enregistrée');

      // Recharger la liste
      await loadVideos();

    } catch (err) {
      console.error('❌ Erreur upload:', err);
      setUploadError('Erreur lors de l\'upload. Vérifiez votre connexion et réessayez.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ─── Ajouter lien YouTube ──────────────────────────────────────────────────
  const handleYoutubeAdd = async () => {
    if (!youtubeUrl.trim()) return;

    const url = youtubeUrl.trim();
    let sourceType = null;

    if (url.match(/^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[\w-]+/)) {
      sourceType = 'youtube';
    } else if (url.match(/^(https?:\/\/)?(www\.)?dailymotion\.com\/video\/[\w-]+/)) {
      sourceType = 'dailymotion';
    } else if (url.match(/^(https?:\/\/)?(www\.)?vimeo\.com\/\d+/)) {
      sourceType = 'vimeo';
    }

    if (!sourceType) {
      setUploadError('URL non supportée. Utilisez YouTube, Dailymotion ou Vimeo.');
      return;
    }

    setUploadError(null);

    try {
      const { error } = await supabase
        .from('match_videos')
        .insert({
          userId:     currentUser.id,
          sourceType,
          sourceUrl:  url,
          status:     'pending',
        });

      if (error) throw error;

      setYoutubeUrl('');
      setShowUrlInput(false);
      await loadVideos();
    } catch (err) {
      console.error('❌ Erreur ajout YouTube:', err);
      setUploadError('Erreur lors de l\'ajout du lien YouTube.');
    }
  };

  // ─── Charger les matchs pour l'association ──────────────────────────────────
  const loadMatches = async () => {
    const { data, error } = await supabase
      .from('matches')
      .select('id, "prenomJoueur1", "joueur1Nom", "prenomJoueur2", "joueur2Nom", "dateMatch", "currentScore"')
      .eq('userId', currentUser.id)
      .order('"dateMatch"', { ascending: false });
    if (!error) setMatches(data || []);
  };

  // ─── Associer vidéo à un match ────────────────────────────────────────────
  const handleAssociate = async (matchId) => {
    setAssociating(true);
    try {
      const { error } = await supabase
        .from('match_videos')
        .update({ matchId })
        .eq('id', associateVideo.id);
      if (error) throw error;
      setAssociateVideo(null);
      setMatchSearch('');
      await loadVideos();
      console.log('✅ Vidéo associée au match:', matchId);
    } catch (err) {
      console.error('❌ Erreur association:', err);
    } finally {
      setAssociating(false);
    }
  };

  // ─── Dissocier vidéo d'un match ───────────────────────────────────────────
  const handleDissociate = async (video) => {
    try {
      const { error } = await supabase
        .from('match_videos')
        .update({ matchId: null })
        .eq('id', video.id);
      if (error) throw error;
      await loadVideos();
      console.log('✅ Vidéo dissociée');
    } catch (err) {
      console.error('❌ Erreur dissociation:', err);
    }
  };

  // ─── Suppression vidéo ───────────────────────────────────────────────────────
  const handleDelete = async (video) => {
    setDeleting(true);
    try {
      // 1. Supprimer dans R2 si un fichier existe
      if (video.storagePath) {
        await deleteVideo(video.storagePath);
      }
      // 2. Supprimer dans Supabase
      const { error } = await supabase
        .from('match_videos')
        .delete()
        .eq('id', video.id);
      if (error) throw error;
      setConfirmDelete(null);
      await loadVideos();
      console.log('✅ Vidéo supprimée:', video.id);
    } catch (err) {
      console.error('❌ Erreur suppression:', err);
      setUploadError('Erreur lors de la suppression.');
    } finally {
      setDeleting(false);
    }
  };

  // ─── Statut visuel ─────────────────────────────────────────────────────────
  const statusLabel = (status) => {
    const map = {
      uploading:  { label: 'Upload...',     color: '#1976D2' },
      pending:    { label: 'En attente',    color: '#FF9800' },
      processing: { label: 'En traitement', color: '#7B1FA2' },
      analyzed:   { label: 'Analysé',       color: '#4CAF50' },
      validated:  { label: 'Validé',        color: '#388E3C' },
      error:      { label: 'Erreur',        color: '#F44336' },
    };
    return map[status] || { label: status, color: '#999' };
  };

  const formatSize = (mb) => {
    if (!mb) return '-';
    return mb >= 1000 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
  };

  // ─── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <button onClick={onBack} style={{
          border: 'none', background: 'none', fontSize: '20px',
          cursor: 'pointer', color: '#666', padding: '4px',
        }}>←</button>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1a1a2e' }}>
          🎬 Mes vidéos
        </h2>
      </div>

      {/* Boutons d'ajout */}
      {!uploading && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
          <label style={{
            flex: 1, padding: '12px', borderRadius: '10px',
            backgroundColor: '#7B1FA2', color: 'white',
            fontSize: '13px', fontWeight: '600', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          }}>
            📁 Importer un fichier
            <input
              type="file"
              accept=".mp4,.mov,.avi,.mkv,.webm"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>

          <button
            onClick={() => { setShowUrlInput(!showUrlInput); setUploadError(null); }}
            style={{
              flex: 1, padding: '12px', borderRadius: '10px',
              backgroundColor: '#C62828', color: 'white',
              fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            }}
          >
            ▶ Lien vidéo
          </button>
        </div>
      )}

      {/* Barre de progression upload */}
      {uploading && (
        <div style={{
          backgroundColor: '#f3e5f5', borderRadius: '10px',
          padding: '16px', marginBottom: '16px',
        }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#7B1FA2', marginBottom: '8px' }}>
            📤 Upload en cours... {uploadProgress}%
          </div>
          <div style={{ backgroundColor: '#e0e0e0', borderRadius: '4px', height: '8px' }}>
            <div style={{
              backgroundColor: '#7B1FA2', borderRadius: '4px', height: '8px',
              width: `${uploadProgress}%`, transition: 'width 0.3s ease',
            }} />
          </div>
          <div style={{ fontSize: '11px', color: '#999', marginTop: '6px' }}>
            Ne fermez pas cette page pendant l'upload
          </div>
        </div>
      )}

      {/* Input YouTube */}
      {showUrlInput && !uploading && (
        <div style={{
          backgroundColor: '#fff3f3', borderRadius: '10px',
          padding: '14px', marginBottom: '16px', border: '1px solid #ffcdd2',
        }}>
          <div style={{ fontSize: '12px', color: '#C62828', fontWeight: '600', marginBottom: '8px' }}>
            Colle un lien YouTube, Dailymotion ou Vimeo
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={youtubeUrl}
              onChange={e => setYoutubeUrl(e.target.value)}
              placeholder="YouTube, Dailymotion ou Vimeo..."
              style={{
                flex: 1, padding: '8px 12px', borderRadius: '8px',
                border: '1px solid #ddd', fontSize: '13px',
              }}
              onKeyDown={e => e.key === 'Enter' && handleYoutubeAdd()}
            />
            <button onClick={handleYoutubeAdd} style={{
              padding: '8px 14px', borderRadius: '8px',
              backgroundColor: '#C62828', color: 'white',
              border: 'none', fontWeight: '600', cursor: 'pointer', fontSize: '13px',
            }}>
              Ajouter
            </button>
          </div>
        </div>
      )}

      {/* Message erreur */}
      {uploadError && (
        <div style={{
          backgroundColor: '#ffebee', color: '#C62828', borderRadius: '8px',
          padding: '10px 12px', fontSize: '13px', marginBottom: '16px',
        }}>
          ⚠️ {uploadError}
        </div>
      )}

      {/* Liste des vidéos */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          Chargement...
        </div>
      ) : videos.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '40px 20px',
          backgroundColor: '#f8f9fa', borderRadius: '12px', color: '#999',
        }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎬</div>
          <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
            Aucune vidéo pour le moment
          </div>
          <div style={{ fontSize: '12px' }}>
            Importe un fichier ou ajoute un lien YouTube pour commencer l'analyse
          </div>
        </div>
      ) : (
        <>
        {selectedVideo && (
          <VideoPlayer
            video={selectedVideo}
            onClose={() => setSelectedVideo(null)}
          />
        )}

        {/* Modal association match */}
        {associateVideo && (
          <div onClick={() => { setAssociateVideo(null); setMatchSearch(''); }} style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              backgroundColor: 'white', borderRadius: '16px', padding: '20px',
              maxWidth: '380px', width: '100%',
              maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', color: '#1a1a2e' }}>🔗 Associer à un match</h3>
                <button onClick={() => { setAssociateVideo(null); setMatchSearch(''); }}
                  style={{ border: 'none', background: 'none', fontSize: '18px', cursor: 'pointer', color: '#999' }}>✕</button>
              </div>

              {/* Recherche */}
              <input
                type="text"
                value={matchSearch}
                onChange={e => setMatchSearch(e.target.value)}
                placeholder="🔍 Rechercher un match..."
                style={{
                  padding: '8px 12px', borderRadius: '8px',
                  border: '1px solid #ddd', fontSize: '13px',
                  marginBottom: '12px', width: '100%', boxSizing: 'border-box',
                }}
              />

              {/* Liste matchs */}
              <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {matches
                  .filter(m => {
                    const name = `${m.prenomJoueur1} ${m.joueur1Nom} ${m.prenomJoueur2} ${m.joueur2Nom}`.toLowerCase();
                    return name.includes(matchSearch.toLowerCase());
                  })
                  .map(m => {
                    const sets = m.currentScore?.sets || [];
                    const score = sets.map(s => `${s[0]}-${s[1]}`).join(' ');
                    const isSelected = associateVideo.matchId === m.id;
                    return (
                      <div
                        key={m.id}
                        onClick={() => !associating && handleAssociate(m.id)}
                        style={{
                          padding: '10px 12px', borderRadius: '8px',
                          border: `2px solid ${isSelected ? '#1976D2' : '#eee'}`,
                          backgroundColor: isSelected ? '#e3f2fd' : 'white',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>
                          {m.prenomJoueur1} {m.joueur1Nom} vs {m.prenomJoueur2} {m.joueur2Nom}
                        </div>
                        <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                          {new Date(m.dateMatch).toLocaleDateString('fr-FR')}
                          {score ? ` · ${score}` : ''}
                        </div>
                      </div>
                    );
                  })}
                {matches.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#999', fontSize: '13px', padding: '20px' }}>
                    Aucun match trouvé
                  </div>
                )}
              </div>

              <button
                onClick={() => { setAssociateVideo(null); setMatchSearch(''); }}
                style={{
                  marginTop: '14px', padding: '10px', borderRadius: '8px',
                  border: '1px solid #ddd', backgroundColor: 'white',
                  fontSize: '13px', cursor: 'pointer', color: '#666',
                }}
              >Annuler</button>
            </div>
          </div>
        )}

        {/* Modal confirmation suppression */}
        {confirmDelete && (
          <div onClick={() => setConfirmDelete(null)} style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              backgroundColor: 'white', borderRadius: '16px', padding: '24px',
              maxWidth: '320px', width: '100%', textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>🗑️</div>
              <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#1a1a2e' }}>
                Supprimer la vidéo ?
              </h3>
              <p style={{ fontSize: '13px', color: '#666', margin: '0 0 20px', lineHeight: '1.5' }}>
                Cette action est irréversible. La vidéo sera supprimée définitivement.
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setConfirmDelete(null)}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '8px',
                    border: '1px solid #ddd', backgroundColor: 'white',
                    fontSize: '13px', cursor: 'pointer', color: '#666',
                  }}
                >Annuler</button>
                <button
                  onClick={() => handleDelete(confirmDelete)}
                  disabled={deleting}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '8px',
                    border: 'none', backgroundColor: '#F44336',
                    color: 'white', fontSize: '13px', fontWeight: '600',
                    cursor: 'pointer', opacity: deleting ? 0.7 : 1,
                  }}
                >
                  {deleting ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {videos.map(video => {
            const st = statusLabel(video.status);
            return (
              <div
                key={video.id}
                onClick={() => video.storagePath || ['youtube', 'dailymotion', 'vimeo'].includes(video.sourceType) ? setSelectedVideo(video) : null}
                style={{
                  backgroundColor: 'white', borderRadius: '10px',
                  padding: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  border: '1px solid #eee',
                  cursor: video.storagePath || ['youtube', 'dailymotion', 'vimeo'].includes(video.sourceType) ? 'pointer' : 'default',
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '13px', fontWeight: '600', color: '#1a1a2e',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      marginBottom: '4px',
                    }}>
                      {['youtube', 'dailymotion', 'vimeo'].includes(video.sourceType) ? '▶ ' : '📁 '}
                      {['youtube', 'dailymotion', 'vimeo'].includes(video.sourceType) ? video.sourceUrl : video.storagePath?.split('/').pop() || 'Vidéo'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#999' }}>
                      {new Date(video.createdAt).toLocaleDateString('fr-FR')}
                      {video.fileSizeMb ? ` · ${formatSize(video.fileSizeMb)}` : ''}
                    </div>
                    {video.matches && (
                      <div style={{
                        fontSize: '11px', color: '#1976D2', fontWeight: '600',
                        marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px',
                      }}>
                        🔗 {video.matches.prenomJoueur1} {video.matches.joueur1Nom} vs {video.matches.prenomJoueur2} {video.matches.joueur2Nom}
                        <span style={{ color: '#999', fontWeight: '400' }}>
                          · {new Date(video.matches.dateMatch).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, marginLeft: '10px' }}>
                    {/* Bouton associer/dissocier */}
                    {video.matchId ? (
                      <button
                        onClick={e => { e.stopPropagation(); handleDissociate(video); }}
                        style={{ border: 'none', backgroundColor: '#e3f2fd', color: '#1976D2', cursor: 'pointer', fontSize: '11px', fontWeight: '600', borderRadius: '6px', padding: '3px 7px' }}
                        title="Dissocier du match"
                      >🔗 Lié</button>
                    ) : (
                      <button
                        onClick={e => { e.stopPropagation(); loadMatches(); setAssociateVideo(video); }}
                        style={{ border: 'none', backgroundColor: '#f0f0f0', color: '#666', cursor: 'pointer', fontSize: '11px', fontWeight: '600', borderRadius: '6px', padding: '3px 7px' }}
                        title="Associer à un match"
                      >🔗</button>
                    )}
                    <div style={{
                      padding: '3px 8px', borderRadius: '20px',
                      backgroundColor: st.color + '20', color: st.color,
                      fontSize: '11px', fontWeight: '600',
                    }}>
                      {st.label}
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); setConfirmDelete(video); }}
                      style={{
                        border: 'none', backgroundColor: 'transparent',
                        cursor: 'pointer', fontSize: '16px', padding: '2px',
                        color: '#ccc', lineHeight: 1,
                      }}
                      title="Supprimer"
                    >🗑️</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}
    </div>
  );
}

export default VideoListPage;